/* eslint-disable
    no-unused-vars,
    prefer-const
*/
// TODO: This file was created by bulk-decaffeinate.
// Fix any style issues and re-enable lint.
/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS201: Simplify complex destructure assignments
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import _ from 'underscore';

import type {Junction} from 'assembly/_shared';
import * as shared from 'assembly/_shared';
import type {AssemblyPlugin} from 'assembly/Asm';
import {assert} from 'Asserts';
import type {Sequence as BioJSSequence} from 'BioJS';
import type {Model as FragmentResult} from 'biojs/FragmentResult';
import type {Model as BioJSSequencePrimer} from 'biojs/SequencePrimer';
import * as AssemblyConstants from 'constants/AssemblyConstants';
import type {Selection} from 'molbio/maps/selectables/Selection';
import * as sequtil from 'molbio/SeqUtil';

const {basePlugin, getFlankingPrimers, getJunctions} = shared;
const {ASSEMBLY_TYPE} = AssemblyConstants;

// A region of the sequence that can still be edited before finalization:
// [start, end, downstream forward primer, upstream reverse primer, upstream fragment end,
// downstream fragment start]
export type EditableRange = [
  number,
  number,
  BioJSSequencePrimer | null,
  BioJSSequencePrimer | null,
  number,
  number,
];

export interface PrimerPlugin extends AssemblyPlugin {
  _getTwoPrimerJunctionRanges(
    sequence: BioJSSequence,
    fwd: BioJSSequencePrimer,
    rev: BioJSSequencePrimer
  ): Array<Array<number>>;
  _editableRanges(sequence: BioJSSequence): Array<EditableRange>;
  _findDeleteRange(
    sequence: BioJSSequence,
    deletionStart: number,
    deletionEnd: number
  ): EditableRange | undefined;
  _findReplaceRange(sequence: BioJSSequence, selection: Selection): EditableRange | undefined;
  _canInsert(sequence: BioJSSequence, selection: Selection, errback: (message: string) => void): boolean;
  _canInitiateReplace(
    sequence: BioJSSequence,
    selection: Selection,
    errback: (message: string) => void
  ): boolean;
}

function replacePrimerRegion(
  sequence: BioJSSequence,
  primer: BioJSSequencePrimer,
  bases: string,
  primerStart: number,
  selStart: number,
  selEnd: number
): string {
  const primerBases = primer.bases();
  const startPos = sequtil.distance(primerStart, selStart, sequence.length(), sequence.circular());
  const endPos = sequtil.distance(primerStart, selEnd, sequence.length(), sequence.circular());
  return primerBases.slice(0, startPos) + bases + primerBases.slice(endPos);
}

function protectPrimerEnds(
  sequence: BioJSSequence,
  start: number,
  end: number,
  fwd: BioJSSequencePrimer | null,
  rev: BioJSSequencePrimer | null
): [number, number] | null {
  assert(start !== end, `protectPrimerEnds called on start = ${start} and end = ${end}`);
  const originalStart = start;
  const originalEnd = end;
  if (fwd != null) {
    end = sequtil.mod(end - 1, sequence.length());
  }
  if (rev != null) {
    start = sequtil.mod(start + 1, sequence.length());
  }
  // Do not want equal start and end after shifting to be interpreted as "entire sequence",
  // hence need to return null so caller knows to return.
  // The cleaner way to check this ends up being making sure "wrapping" is preserved.
  if (sequtil.isWrap(originalStart, originalEnd) !== sequtil.isWrap(start, end)) {
    return null;
  }
  return [start, end];
}

function findJunctionAtPosition(sequence: BioJSSequence, position: number): Junction | undefined {
  // eslint-disable-next-line no-restricted-syntax
  return _.find(getJunctions(sequence), (junction) => {
    const [junction3, junction5] = Array.from(junction) as Junction;
    return sequtil.baseInRange(position, junction3, sequtil.mod(junction5 + 1, sequence.length()));
  });
}

// eslint-disable-next-line no-restricted-syntax
const primerPlugin: PrimerPlugin = _.extend({}, basePlugin, {
  _getTwoPrimerJunctionRanges(
    sequence: BioJSSequence,
    fwd: BioJSSequencePrimer,
    rev: BioJSSequencePrimer
  ): never {
    throw new Error(`Did not expect _getTwoPrimerJunctionRanges to be called on primerPlugin - \
make sure it's been implemented`);
  },

  // Finds ranges that can be edited prior to finalization.
  //   - For a junction with two primers, this corresponds to _getTwoPrimerJunctionRanges of the
  //     two primers' ranges.
  //   - For a junction with a primer on the 5' side and an enzyme on the 3' side, this
  //     corresponds to the upstream overhangEdge of the primer up until the 5' junction of the
  //     next parent.
  //   - For a junction with an enzyme on the 5' side, this corresponds to the reverse.
  _editableRanges(sequence: BioJSSequence): Array<EditableRange> {
    const fragmentResults = sequence.fragmentResults();
    return fragmentResults
      .chain()
      .map((fragmentResult: FragmentResult, i: number) => {
        const nextFragmentResult = fragmentResults.at((i + 1) % fragmentResults.length);
        const [_fwd, rev] = Array.from(getFlankingPrimers(sequence, fragmentResult));
        const [nextFwd, _nextRev] = Array.from(getFlankingPrimers(sequence, nextFragmentResult));
        const ranges: Array<Array<number>> =
          rev != null && nextFwd != null
            ? this._getTwoPrimerJunctionRanges(sequence, nextFwd, rev)
            : nextFwd != null
              ? [[fragmentResult.end()!, nextFwd.end()]]
              : rev != null
                ? [[rev.start(), nextFragmentResult.start()!]]
                : [];
        // eslint-disable-next-line no-restricted-syntax
        return _.map(ranges, (...args): EditableRange => {
          const [start, end] = Array.from(args[0]);
          return [start, end, nextFwd, rev, fragmentResult.end()!, nextFragmentResult.start()!];
        });
      })
      .flatten(true)
      .value();
  },

  // Takes in deletionStart and deletionEnd instead of a selection object because, after
  // deletion, the end no longer matches up to the selection.
  _findDeleteRange(
    sequence: BioJSSequence,
    deletionStart: number,
    deletionEnd: number
  ): EditableRange | undefined {
    // eslint-disable-next-line no-restricted-syntax
    return _.find(this._editableRanges(sequence), (range) => {
      let [start, end, fwd, rev, junction3, junction5] = Array.from(range) as EditableRange;
      const ends = protectPrimerEnds(sequence, start, end, fwd, rev);
      if (ends == null) {
        return false;
      }
      [start, end] = Array.from(ends);
      if (sequtil.isRangeWithin(deletionStart, deletionEnd, start, end)) {
        const legalStart = sequtil.mod(junction3 - 1, sequence.length());
        const legalEnd = sequtil.mod(junction5 + 1, sequence.length());
        return sequtil.isRangeOverlap(legalStart, legalEnd, deletionStart, deletionEnd);
      }
      return false;
    });
  },

  _findReplaceRange(sequence: BioJSSequence, selection: Selection): EditableRange | undefined {
    // eslint-disable-next-line no-restricted-syntax
    return _.find(this._editableRanges(sequence), (range) => {
      let [start, end, fwd, rev, _junction3, _junction5] = Array.from(range) as EditableRange;
      const ends = protectPrimerEnds(sequence, start, end, fwd, rev);
      if (ends == null) {
        return false;
      }
      [start, end] = Array.from(ends);
      return sequtil.isRangeWithin(selection.modelStart()!, selection.modelEnd()!, start, end);
    });
  },

  _canInsert(sequence: BioJSSequence, selection: Selection, errback: (message: string) => void): never {
    throw new Error(`Did not expect _canInsert to be called on primerPlugin - \
make sure it's been implemented`);
  },

  _canInitiateReplace(
    sequence: BioJSSequence,
    selection: Selection,
    errback: (message: string) => void
  ): boolean {
    const range = this._findReplaceRange(sequence, selection);
    if (range != null) {
      return true;
    } else {
      if (sequence.assemblyType() === ASSEMBLY_TYPE.GOLDEN_GATE) {
        errback(
          "To replace bases that aren't under a primer's binding bases, finalize the sequence from the " +
            'results tab.'
        );
      } else {
        errback('To replace bases outside junctions, finalize the sequence from the results tab.');
      }
      return false;
    }
  },

  canEditSelection(
    sequence: BioJSSequence,
    selection: Selection,
    errback: (message: string) => void
  ): boolean {
    if (sequence.finalized()) {
      return true;
    }
    if (selection.isEmpty()) {
      return this._canInsert(sequence, selection, errback);
    }
    // Range is selected.
    return this._canInitiateReplace(sequence, selection, errback);
  },

  canReplace(
    sequence: BioJSSequence,
    selection: Selection,
    bases: string,
    errback: (message: string) => void
  ): boolean {
    if (sequence.finalized()) {
      return true;
    }
    const range = this._findReplaceRange(sequence, selection);
    if (range == null) {
      errback('To replace bases outside junctions, finalize the sequence from the results tab.');
      return false;
    }
    const [_start, _end, fwdPrimer, revPrimer] = Array.from(range) as EditableRange;
    // Only check replacement length if there are primers.
    if (!(fwdPrimer != null || revPrimer != null)) {
      return true;
    }

    const dist = sequtil.distance(
      selection.modelStart()!,
      selection.modelEnd()!,
      sequence.length(),
      sequence.circular()
    );
    if (dist === bases.length) {
      return true;
    } else {
      errback('Replacement bases in regions must be the same length as the selection.');
      return false;
    }
  },
});

export {findJunctionAtPosition, primerPlugin, replacePrimerRegion};
